import React, { useState, useEffect } from "react";
import { AlertTriangle, Menu, PanelLeft, Share2, Copy, Check, X } from "lucide-react";
import "./App.css";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { setAuthToken, sendMessage, shareChat, getThinkingSteps, getGreeting, getSessions, getProfile, getSessionMessages, deleteSession } from "./services/api";
import Login from "./components/Login";
import SignUp from "./components/SignUp";
import Sidebar from "./components/Sidebar";
import ChatWindow from "./components/ChatWindow";
import InputBox from "./components/InputBox";
import SourcesPanel from "./components/SourcesPanel";
import TemplatesPanel from "./components/TemplatesPanel";
import PDFUploader from "./components/PDFUploader";
import Logo from "./components/Logo";
import SplashScreen from "./components/SplashScreen";
import SharedChatView from "./components/SharedChatView";
import LevelModal from "./components/LevelModal";

// ── Auth gate — goes directly to Login ────────────────────────────────────────

function AuthGate() {
  const [authView, setAuthView] = useState("login"); // login | signup

  if (authView === "login") {
    return (
      <Login
        onSwitchToSignUp={() => setAuthView("signup")}
      />
    );
  }

  return (
    <SignUp
      onSwitchToLogin={() => setAuthView("login")}
    />
  );
}

// ── Toast component ──────────────────────────────────────────────────────────

function Toast({ message, onDone }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLeaving(true), 2200);
    const remove = setTimeout(() => onDone?.(), 2500);
    return () => { clearTimeout(timer); clearTimeout(remove); };
  }, [onDone]);

  return (
    <div className={`toast-notification ${leaving ? "toast-out" : ""}`}>
      <Check size={14} /> {message}
    </div>
  );
}

// ── Share Modal ──────────────────────────────────────────────────────────────

function ShareModal({ shareUrl, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <h3><Share2 size={18} /> Share Chat</h3>
        <p>Anyone with this link can view this conversation.</p>
        <div className="share-link-row">
          <input
            className="share-link-input"
            value={shareUrl}
            readOnly
            onFocus={(e) => e.target.select()}
          />
          <button className="share-copy-btn" onClick={handleCopy}>
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
        </div>
        <button className="share-close-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ── Main app (authenticated) ──────────────────────────────────────────────────

const DEFAULT_THINKING_STEPS = [
  "Analyzing legal query...",
  "Searching jurisprudential records...",
  "Preparing comprehensive synthesis..."
];

function AppContent() {
  const { user, token, loading, logout } = useAuth();
  const [splashText, setSplashText] = useState("Preparing your legal workspace...");
  const [showSplash, setShowSplash] = useState(false);
  const [wasAuthGateShown, setWasAuthGateShown] = useState(false);
  const [pendingLogout, setPendingLogout] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);

  const prevUserRef = React.useRef(user);

  useEffect(() => {
    if (!prevUserRef.current && user) {
      if (wasAuthGateShown) {
        setSplashText("Preparing your legal workspace...");
        setShowSplash(true);
      }
    }
    prevUserRef.current = user;
  }, [user, wasAuthGateShown]);

  const handleLogout = () => {
    setSplashText("Signing out of Prakarna AI...");
    setShowSplash(true);
    setPendingLogout(true);
  };

  const handleSplashComplete = () => {
    if (pendingLogout) {
      setPendingLogout(false);
      setShowSplash(false);
      logout();
    } else {
      sessionStorage.setItem('dharma-splash-seen', 'true');
      setShowSplash(false);
    }
  };

  // Sync Firebase token into axios headers
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  const [userProfile, setUserProfile] = useState(null);

  const [session, setSession] = useState({
    userId: null,
    chats: [{ id: "initial", title: "New Chat", messages: [], loaded: true }],
    activeChatId: "initial",
  });
  const { chats, activeChatId } = session;

  // Load sessions + profile from backend when user logs in
  useEffect(() => {
    const currentUid = user ? user.uid : null;
    if (session.userId !== currentUid) {
      if (currentUid) {
        // Load chat sessions from backend
        getSessions().then(({ sessions }) => {
          const backendChats = sessions.map((s) => ({
            id: s.session_id,
            title: s.title,
            messages: [],
            loaded: false,
          }));
          const chatsToSet = backendChats.length > 0
            ? backendChats
            : [{ id: "initial-" + Date.now(), title: "New Chat", messages: [], loaded: true }];
          setSession({
            userId: currentUid,
            chats: chatsToSet,
            activeChatId: chatsToSet[0].id,
          });
        }).catch(() => {
          setSession({
            userId: currentUid,
            chats: [{ id: "initial-" + Date.now(), title: "New Chat", messages: [], loaded: true }],
            activeChatId: "initial-" + Date.now(),
          });
        });

        // Load profile from backend
        getProfile().then((profile) => {
          setUserProfile(profile);
          if (!profile.level) setShowLevelModal(true);
        }).catch(() => {
          setShowLevelModal(true);
        });
      } else {
        setSession({
          userId: null,
          chats: [{ id: "initial", title: "New Chat", messages: [], loaded: true }],
          activeChatId: "initial",
        });
        setUserProfile(null);
      }
    }
  }, [user, session.userId]);
  const [isLoadingReply, setIsLoadingReply] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState(DEFAULT_THINKING_STEPS);
  const [abortController, setAbortController] = useState(null);
  const [activePanel, setActivePanel] = useState("chat");
  const [prefillText, setPrefillText] = useState("");
  const [error, setError] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [toast, setToast] = useState("");
  const [shareModal, setShareModal] = useState(null); // { shareUrl }
  const [selectedModel, setSelectedModel] = useState("llama-3.3-70b-versatile");

  // Default to light mode for first-time users
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("dharma-theme");
    if (saved) return saved;
    // First time user → light mode
    return "light";
  });

  useEffect(() => {
    localStorage.setItem("dharma-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const activeChat = chats.find((c) => c.id === activeChatId) || chats[0];
  const messages = activeChat.messages;
  const latestSources = [...messages].reverse().find((m) => m.role === "assistant")?.sources || [];

  const getProfileName = () => {
    try {
      const profileKey = user ? `dharma-profile-${user.uid}` : "dharma-profile";
      const savedProfile = JSON.parse(localStorage.getItem(profileKey) || "{}");
      if (savedProfile.name) return savedProfile.name;
    } catch { }
    return user?.displayName || user?.email?.split("@")[0] || "Ram";
  };
  const profileName = getProfileName();

  const [greetingText, setGreetingText] = useState("");

  useEffect(() => {
    if (!profileName) return;
    const now = new Date();

    // Cache by time period (morning, afternoon, evening, night)
    const hour = now.getHours();
    const timePeriod = (hour >= 5 && hour < 12) ? "morning" : (hour >= 12 && hour < 17) ? "afternoon" : (hour >= 17 && hour < 22) ? "evening" : "night";
    const dateKey = now.toISOString().slice(0, 10);
    const cacheKey = user ? `dharma-greeting-${user.uid}-${dateKey}-${timePeriod}` : `dharma-greeting-${dateKey}-${timePeriod}`;

    const fallback = `Welcome back, ${profileName}. What legal topic would you like to explore today?`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setGreetingText(cached);
      return;
    }

    // Determine context
    const isFirstVisit = !localStorage.getItem("dharma-has-visited");
    if (isFirstVisit) localStorage.setItem("dharma-has-visited", "true");

    const lastGreeting = localStorage.getItem("dharma-last-greeting") || "";
    const recentTopic = session?.chats?.find(c => c.messages?.length > 0)?.title || "";

    // Format current time (HH:MM AM/PM) and day of week, appending time period for LLM context
    const formattedTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    const currentTime = `${formattedTime} (${timePeriod})`;
    const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });

    setGreetingText(fallback);
    getGreeting({
      first_name: profileName,
      current_time: currentTime,
      day_of_week: dayOfWeek,
      is_first_visit: isFirstVisit,
      recent_topic: recentTopic,
      last_greeting: lastGreeting
    })
      .then((result) => {
        const greeting = result?.greeting || fallback;
        localStorage.setItem(cacheKey, greeting);
        localStorage.setItem("dharma-last-greeting", greeting);
        setGreetingText(greeting);
      })
      .catch((err) => {
        console.error("Greeting generation failed:", err);
      });
  }, [profileName, user, session?.chats]);

  if (loading) {
    return (
      <div className="app-skeleton-layout" data-theme={theme}>
        <aside className="sidebar skeleton-sidebar">
          <div className="skeleton-brand-row">
            <div className="skeleton-logo-wrap"><Logo size={22} className="skeleton-logo" /></div>
            <div className="skeleton-brand-copy">
              <span>Prakarna AI</span>
              <small>Preparing workspace</small>
            </div>
          </div>
          <div className="skeleton-new-chat shimmer" />
          <div className="skeleton-search shimmer" />
          <div className="sidebar-section skeleton-nav-list">
            <div className="skeleton-section-label shimmer" />
            {[0, 1, 2].map((item) => (
              <div key={item} className="skeleton-nav-item shimmer" />
            ))}
          </div>
          <div className="skeleton-sidebar-footer">
            <div className="skeleton-footer-btn shimmer" />
            <div className="skeleton-footer-btn shimmer" />
            <div className="skeleton-profile-card shimmer" />
          </div>
        </aside>

        <main className="main-area skeleton-main">
          <div className="mobile-header skeleton-mobile-header">
            <Logo size={20} className="skeleton-logo" />
            <strong>Prakarna AI</strong>
          </div>
          <div className="skeleton-chat-stage">
            <div className="skeleton-chat-orb">
              <Logo size={30} className="skeleton-logo" />
            </div>
            <div className="skeleton-chat-title">Preparing your legal workspace</div>
            <div className="skeleton-chat-subtitle">Loading research tools, sources, and your recent context.</div>
            <div className="skeleton-message-list">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="skeleton-message-card">
                  <div className="skeleton-avatar">
                    {item % 2 === 0 ? <Logo size={16} className="skeleton-logo" /> : <span />}
                  </div>
                  <div className="skeleton-text-lines">
                    <div className="skeleton-line shimmer skeleton-line-long" />
                    <div className="skeleton-line shimmer skeleton-line-short" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="skeleton-input-shell">
            <div className="skeleton-input shimmer" />
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    if (!wasAuthGateShown) {
      setWasAuthGateShown(true);
    }
    return <AuthGate />;
  }

  if (showSplash) {
    return (
      <SplashScreen
        text={splashText}
        onComplete={handleSplashComplete}
      />
    );
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  const updateActiveChat = (newMessages, newTitle = activeChat.title) => {
    setSession((prev) => ({
      ...prev,
      chats: prev.chats.map((c) =>
        c.id === prev.activeChatId ? { ...c, messages: newMessages, title: newTitle } : c
      ),
    }));
  };

  const handleStop = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoadingReply(false);
      setError("Response cancelled by user.");
    }
  };

  const handleStreamEvent = (event) => {
    if (["metadata", "token", "chunk", "status", "activity"].includes(event.type)) {
      setIsLoadingReply(false);
    }

    setSession((prev) => {
      const currentChats = prev.chats;
      const activeChatIndex = currentChats.findIndex((c) => c.id === prev.activeChatId);
      if (activeChatIndex === -1) return prev;

      const chat = currentChats[activeChatIndex];
      const msgs = [...chat.messages];

      if (msgs[msgs.length - 1]?.role !== "assistant") {
        msgs.push({
          role: "assistant",
          content: "",
          intent: "",
          sources: [],
          citations: [],
          suggested_questions: [],
          live_activity: "Preparing legal research...",
          streaming: true,
        });
      }

      const lastMsg = { ...msgs[msgs.length - 1] };

      if (event.type === "metadata") {
        lastMsg.intent = event.data?.intent || "";
        lastMsg.sources = event.data?.sources || [];
        lastMsg.response_id = event.data?.response_id;
        lastMsg.timestamp = event.data?.timestamp;
        lastMsg.live_activity = event.data?.activity || "Generating response...";
        lastMsg.streaming = true;
      } else if (event.type === "token" || event.type === "chunk") {
        lastMsg.content += event.data || "";
        lastMsg.live_activity = "";
        lastMsg.streaming = true;
      } else if (event.type === "citations") {
        lastMsg.citations = event.data || [];
      } else if (event.type === "suggestions") {
        lastMsg.suggested_questions = event.data || [];
      } else if (event.type === "done") {
        lastMsg.streaming = false;
        lastMsg.live_activity = "";
      } else if (event.type === "status" || event.type === "activity") {
        lastMsg.live_activity = event.data || "";
      } else if (event.type === "info") {
        if (event.data?.type === "fallback") {
          setToast(`⚡ Switched to ${event.data.model} due to rate limits`);
          setTimeout(() => setToast(""), 4000);
        }
      }

      msgs[msgs.length - 1] = lastMsg;

      const newChats = [...currentChats];
      newChats[activeChatIndex] = { ...chat, messages: msgs };

      return { ...prev, chats: newChats };
    });
  };

  const appendAssistantError = (errText) => {
    setSession((prev) => {
      const currentChats = prev.chats;
      const activeChatIndex = currentChats.findIndex((c) => c.id === prev.activeChatId);
      if (activeChatIndex === -1) return prev;

      const chat = currentChats[activeChatIndex];
      const msgs = [...chat.messages];

      if (msgs[msgs.length - 1]?.role !== "assistant") {
        msgs.push({
          role: "assistant",
          content: "",
          intent: "",
          sources: [],
          citations: [],
          suggested_questions: [],
        });
      }

      const lastMsg = { ...msgs[msgs.length - 1] };
      lastMsg.content = (lastMsg.content ? lastMsg.content + "\n\n" : "") + `**Error:** ${errText}`;
      lastMsg.streaming = false;
      lastMsg.live_activity = "";
      msgs[msgs.length - 1] = lastMsg;

      const newChats = [...currentChats];
      newChats[activeChatIndex] = { ...chat, messages: msgs };
      return { ...prev, chats: newChats };
    });
  };

  const handleSend = async (text, attachments = []) => {
    if (isLoadingReply) {
      handleStop();
      return;
    }

    const processedAttachments = [];
    for (const att of attachments) {
      let previewData = att.preview;
      if (att.file && att.type?.startsWith("image/")) {
        try {
          previewData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(att.file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
          });
        } catch (e) {
          console.error("Base64 conversion failed", e);
        }
      }
      processedAttachments.push({
        name: att.name,
        size: att.size,
        type: att.type,
        preview: previewData || att.preview,
      });
    }

    const attachmentLabel = attachments.length
      ? `

Attached: ${attachments.map((att) => att.name).join(", ")}`
      : "";
    const displayText = `${text}${attachmentLabel}`.trim() || "Attached files";
    const userMsg = {
      role: "user",
      content: displayText,
      attachments: processedAttachments
    };
    const updatedMessages = [...messages, userMsg];

    let newTitle = activeChat.title;
    if (messages.length === 0) {
      newTitle = displayText.length > 35 ? displayText.slice(0, 35) + "…" : displayText;
    }

    updateActiveChat(updatedMessages, newTitle);

    setIsLoadingReply(true);
    setThinkingSteps(DEFAULT_THINKING_STEPS);
    getThinkingSteps(text || "Analyzing attached files")
      .then((steps) => {
        if (Array.isArray(steps) && steps.length > 0) {
          setThinkingSteps(steps);
        }
      })
      .catch((err) => console.error("Thinking steps fetch failed:", err));
    setError("");
    setActivePanel("chat");

    const controller = new AbortController();
    setAbortController(controller);

    const history = updatedMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      let savedProfile = {};
      try {
        const profileKey = user ? `dharma-profile-${user.uid}` : "dharma-profile";
        savedProfile = JSON.parse(localStorage.getItem(profileKey) || "{}");
      } catch {
        savedProfile = {};
      }
      const response = await sendMessage(
        text,
        history,
        activeChatId,
        controller.signal,
        attachments,
        savedProfile.level || null,
        handleStreamEvent,
        selectedModel
      );
      // The assistant message is built via handleStreamEvent as it streams.
      // We just need to ensure the title is updated for new chats without overwriting messages
      // using a functional state update to avoid stale closures.
      if (messages.length === 0) {
        setSession((prev) => ({
          ...prev,
          chats: prev.chats.map((c) =>
            c.id === activeChatId ? { ...c, title: newTitle } : c
          ),
        }));
      }
    } catch (err) {
      if (err.name === "AbortError" || err.message === "canceled") {
        console.log("Request aborted");
        return;
      }
      const errText = err.message || "Backend error. Is your FastAPI server running on port 8000?";
      setError(errText);
      appendAssistantError(errText);
    } finally {
      setIsLoadingReply(false);
      setAbortController(null);
    }
  };

  // ── Edit message (ChatGPT-style: truncate + regenerate) ────────────────────

  const handleEditMessage = async (index, newContent) => {
    const truncated = messages.slice(0, index);
    const originalMsg = messages[index];
    const originalAttachments = originalMsg?.attachments || [];

    const attachmentLabel = originalAttachments.length
      ? `

Attached: ${originalAttachments.map((att) => att.name).join(", ")}`
      : "";
    const fullContent = `${newContent}${attachmentLabel}`.trim();

    const editedUserMsg = {
      role: "user",
      content: fullContent,
      attachments: originalAttachments
    };
    const updatedMessages = [...truncated, editedUserMsg];

    let newTitle = activeChat.title;
    if (index === 0) {
      newTitle = newContent.length > 35 ? newContent.slice(0, 35) + "…" : newContent;
    }

    updateActiveChat(updatedMessages, newTitle);

    setIsLoadingReply(true);
    setThinkingSteps(DEFAULT_THINKING_STEPS);
    getThinkingSteps(newContent || "Regenerating response")
      .then((steps) => {
        if (Array.isArray(steps) && steps.length > 0) {
          setThinkingSteps(steps);
        }
      })
      .catch((err) => console.error("Thinking steps fetch failed:", err));
    setError("");
    setActivePanel("chat");

    const controller = new AbortController();
    setAbortController(controller);

    const history = updatedMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      let savedProfile = {};
      try {
        const profileKey = user ? `dharma-profile-${user.uid}` : "dharma-profile";
        savedProfile = JSON.parse(localStorage.getItem(profileKey) || "{}");
      } catch {
        savedProfile = {};
      }
      const response = await sendMessage(
        newContent,
        history,
        activeChatId,
        controller.signal,
        [],
        savedProfile.level || null
      );
      updateActiveChat(
        [
          ...updatedMessages,
          {
            role: "assistant",
            content: response.answer,
            intent: response.intent,
            sources: response.sources || [],
            citations: response.citations || [],
            suggested_questions: response.suggested_questions || [],
          },
        ],
        newTitle
      );
    } catch (err) {
      if (err.name === "AbortError" || err.message === "canceled") {
        console.log("Request aborted");
        return;
      }
      const errText = err.message || "Backend error. Is your FastAPI server running on port 8000?";
      setError(errText);
      appendAssistantError(errText);
    } finally {
      setIsLoadingReply(false);
      setAbortController(null);
    }
  };

  const handleRegenerate = async (index) => {
    if (index > 0 && messages[index - 1]) {
      await handleEditMessage(index - 1, messages[index - 1].content);
    }
  };

  // ── Share chat ─────────────────────────────────────────────────────────────

  const handleShareChat = async () => {
    if (messages.length === 0) return;
    try {
      const shareable = messages.map((m) => ({
        role: m.role,
        content: m.content,
        intent: m.intent || undefined,
      }));
      const result = await shareChat(activeChat.title, shareable);
      setShareModal({ shareUrl: result.share_url });
    } catch (err) {
      console.error("Share failed:", err);
      setToast("Failed to create share link");
    }
  };

  const handleNewChat = () => {
    if (messages.length > 0) {
      const newId = Date.now().toString();
      setSession((prev) => ({
        ...prev,
        chats: [{ id: newId, title: "New Chat", messages: [], loaded: true }, ...prev.chats],
        activeChatId: newId,
      }));
    }
    setError("");
    setPrefillText("");
    setActivePanel("chat");
  };

  const switchChat = async (id) => {
    setSession((prev) => ({ ...prev, activeChatId: id }));
    setActivePanel("chat");
    setIsMobileMenuOpen(false);

    // Lazy-load messages from backend if not yet fetched
    const chat = session.chats.find((c) => c.id === id);
    if (chat && !chat.loaded) {
      try {
        const data = await getSessionMessages(id);
        setSession((prev) => ({
          ...prev,
          chats: prev.chats.map((c) =>
            c.id === id
              ? { ...c, messages: data.messages.map((m) => ({ role: m.role, content: m.content })), loaded: true }
              : c
          ),
        }));
      } catch {
        setSession((prev) => ({
          ...prev,
          chats: prev.chats.map((c) => (c.id === id ? { ...c, loaded: true } : c)),
        }));
      }
    }
  };

  const deleteChat = (id) => {
    deleteSession(id).catch(() => { });
    setSession((prev) => {
      const updated = prev.chats.filter((c) => c.id !== id);
      let newActiveId = prev.activeChatId;
      let newChats = updated;
      if (updated.length === 0) {
        const newId = Date.now().toString();
        newChats = [{ id: newId, title: "New Chat", messages: [], loaded: true }];
        newActiveId = newId;
      } else if (prev.activeChatId === id) {
        newActiveId = updated[0].id;
      }
      return { ...prev, chats: newChats, activeChatId: newActiveId };
    });
  };

  const renameChat = (id, newTitle) =>
    setSession((prev) => ({
      ...prev,
      chats: prev.chats.map((c) => (c.id === id ? { ...c, title: newTitle } : c)),
    }));

  const handleExport = () => {
    const lines = messages.map((m) => {
      const role = m.role === "user" ? "YOU" : "DHARMAAI";
      const badge = m.intent ? ` [${m.intent.toUpperCase()}]` : "";
      return `${role}${badge}:\n${m.content}\n`;
    });
    const blob = new Blob([lines.join("\n---\n\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dharmaai-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`app-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`} data-theme={theme}>
      {isMobileMenuOpen && (
        <div className="mobile-backdrop" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      <Sidebar
        chats={chats}
        activeChatId={activeChatId}
        onNewChat={() => { handleNewChat(); setIsMobileMenuOpen(false); }}
        onSwitchChat={switchChat}
        onDeleteChat={deleteChat}
        onRenameChat={renameChat}
        onExport={handleExport}
        activePanel={activePanel}
        setActivePanel={(panel) => { setActivePanel(panel); setIsMobileMenuOpen(false); }}
        isExportDisabled={messages.length === 0}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onOpenUploader={() => setShowUploader(true)}
        theme={theme}
        onToggleTheme={toggleTheme}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
        onLogout={handleLogout}
      />

      <main className="main-area">
        {/* Floating sidebar open button (visible when collapsed) */}
        {isSidebarCollapsed && (
          <div className="tooltip-wrap" style={{ position: "absolute", top: "14px", left: "14px", zIndex: 50 }}>
            <button
              className="sidebar-open-btn"
              onClick={() => setIsSidebarCollapsed(false)}
              style={{ position: "static" }}
            >
              <PanelLeft size={20} />
            </button>
            <div className="tooltip-content tooltip-bottom-left">
              Open sidebar
            </div>
          </div>
        )}

        <div className="mobile-header">
          <button className="menu-btn" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu size={24} />
          </button>
          <div className="mobile-header-brand">
            <Logo size={20} />
            <strong>Prakarna AI</strong>
          </div>
        </div>

        {activePanel === "chat" && (
          messages.length === 0 ? (
            <div className="centered-welcome-container">
              <div className="centered-welcome-content">
                <h1 className="centered-greeting">
                  {greetingText || `Ask away, ${profileName}!`}
                </h1>
                <InputBox
                  onSend={handleSend}
                  loading={isLoadingReply}
                  prefillText={prefillText}
                  onPrefillUsed={() => setPrefillText("")}
                  messages={messages}
                  onCompactContext={handleNewChat}
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                />
              </div>
            </div>
          ) : (
            <>
              <ChatWindow
                messages={messages}
                loading={isLoadingReply}
                onFeatureClick={(prefix) => { setPrefillText(prefix); setActivePanel("chat"); }}
                userName={profileName}
                onEditMessage={handleEditMessage}
                onShareChat={handleShareChat}
                sessionId={activeChatId}
                onRegenerate={handleRegenerate}
                onSendSuggested={handleSend}
                thinkingSteps={thinkingSteps}
              />
              {error && (
                <div className="error-banner">
                  <AlertTriangle size={16} style={{ marginRight: 6 }} /> {error}
                </div>
              )}
              <InputBox
                onSend={handleSend}
                loading={isLoadingReply}
                prefillText={prefillText}
                onPrefillUsed={() => setPrefillText("")}
                messages={messages}
                onCompactContext={handleNewChat}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
              />
            </>
          )
        )}

        {activePanel === "sources" && (
          <div className="panel-page">
            <SourcesPanel
              sources={latestSources}
              onDocumentClick={(title) => {
                setPrefillText(`Tell me more about ${title}: `);
                setActivePanel("chat");
              }}
            />
          </div>
        )}

        {activePanel === "templates" && (
          <div className="panel-page">
            <TemplatesPanel
              onUseTemplate={(prefix) => { setPrefillText(prefix); setActivePanel("chat"); }}
            />
          </div>
        )}
      </main>

      {showUploader && <PDFUploader onClose={() => setShowUploader(false)} />}

      {/* Share modal */}
      {shareModal && (
        <ShareModal
          shareUrl={shareModal.shareUrl}
          onClose={() => setShareModal(null)}
        />
      )}

      {/* Toast notification */}
      {toast && <Toast message={toast} onDone={() => setToast("")} />}

      {/* Level Selection Modal */}
      {showLevelModal && (
        <LevelModal onSave={() => setShowLevelModal(false)} />
      )}
    </div>
  );
}

// ── Root with provider ────────────────────────────────────────────────────────

export default function App() {
  // Check for shared chat URL parameter
  const params = new URLSearchParams(window.location.search);
  const shareId = params.get("share");

  if (shareId) {
    return <SharedChatView shareId={shareId} />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
